import React, { useState, useEffect, useMemo } from "react";
import { Dropdown, Option, Button } from "@fluentui/react-components";
import {
  Dismiss20Regular,
  PlayRegular,
  PauseRegular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import "../CSS/QueueForm.css";

// Musique d'attente par défaut Heedify : toujours proposée dans le dropdown,
// indépendamment des médias du tenant.
const DEFAULT_MOH = {
  header: "default-moh",
  url: "https://callcenterconf.blob.core.windows.net/heedifymedia/defaultMoH/default-Heedify-Moh.wav",
};

export default function HoldOnMusic(props) {
  const { t } = useTranslation();

  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [audio, setAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioCache, setAudioCache] = useState({});

  // Utiliser directement props.queue.musicOnHold comme source de vérité
  const selectedMusics = props.queue?.musicOnHold || [];

  // Liste des médias = médias du tenant + la musique par défaut Heedify (toujours présente,
  // dédoublonnée par header au cas où le tenant l'aurait déjà).
  const allMedias = useMemo(() => {
    const base = props.medias || [];
    return base.some((m) => m.header === DEFAULT_MOH.header) ? base : [DEFAULT_MOH, ...base];
  }, [props.medias]);



  // --- Sélection d’une nouvelle musique ---
  const handleSelect = (_, data) => {
    const selected = allMedias.find((m) => m.header === data.optionValue);
    if (selected && !selectedMusics.some((m) => m.header === selected.header)) {

      // Mettre à jour le queue avec tableau contenant l'objet musique
      props.setQueue((prev) => ({
        ...prev,
        musicOnHold: [...(prev.musicOnHold || []), selected],
      }));
    }
  };


  // --- Lecture / Pause ---
  const handlePlayPause = async (music) => {
    // Si c'est la même musique, faire pause/play
    if (currentPlaying?.header === music.header && audio) {
      if (audio.paused) {
        try {
          await audio.play();
          setIsPlaying(true);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error("Erreur lors de la lecture:", error);
          }
          setIsPlaying(false);
        }
      } else {
        audio.pause();
        setIsPlaying(false);
      }
      return;
    }

    // Arrêter l'audio précédent s'il joue
    if (audio && !audio.paused) {
      audio.pause();
      setIsPlaying(false);
    }

    // Récupérer ou créer l'audio pour cette musique
    let targetAudio = audioCache[music.header];
    if (!targetAudio) {
      targetAudio = new Audio(music.url);
      targetAudio.onended = () => {
        if (currentPlaying?.header === music.header) {
          setCurrentPlaying(null);
          setAudio(null);
          setIsPlaying(false);
        }
      };
      setAudioCache(prev => ({ ...prev, [music.header]: targetAudio }));
    } else {
      // Remettre au début si on rejoue une musique déjà chargée
      targetAudio.currentTime = 0;
    }

    setAudio(targetAudio);
    setCurrentPlaying(music);
    
    try {
      await targetAudio.play();
      setIsPlaying(true);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Erreur lors de la lecture:", error);
      }
      setCurrentPlaying(null);
      setAudio(null);
      setIsPlaying(false);
    }
  };

  // --- Suppression ---
  const handleDelete = (music) => {
    if (currentPlaying?.header === music.header && audio) {
      audio.pause();
      setCurrentPlaying(null);
      setAudio(null);
      setIsPlaying(false);
    }
    
    // Nettoyer l'audio du cache si elle existe
    if (audioCache[music.header]) {
      const cachedAudio = audioCache[music.header];
      cachedAudio.pause();
      cachedAudio.src = '';
      setAudioCache(prev => {
        const newCache = { ...prev };
        delete newCache[music.header];
        return newCache;
      });
    }
    
    // Supprimer du queue
    props.setQueue((prev) => ({
      ...prev,
      musicOnHold: (prev.musicOnHold || []).filter((m) => m.header !== music.header),
    }));
  };
  return (
    <div className="hold-on-music-section">
      {/* Dropdown */}
      <Dropdown
        className="dropdown-inline"
        placeholder={t("SELECT_MUSIC")}
        selected
        disabled={props.isUpdate ? !props.permissions?.canUpdate : !props.permissions?.canCreate}
        selectedOptions={selectedMusics.map((m) => m.header)}
        onOptionSelect={handleSelect}

      >
        {allMedias.map((item) => (
          <Option key={item.header} value={item.header}>
            {item.header}
          </Option>
        ))}
      </Dropdown>

      {/* Liste */}
      <div className="music-list">
        {props.queue?.musicOnHold.map((music, index) => (
          <div key={index} className="music-item">
            <span className="music-name">{music.header}</span>

            <Button
              appearance="subtle"
              className="btn-play-stop-style"
              icon={
                currentPlaying?.header === music.header && isPlaying ? (
                  <PauseRegular />
                ) : (
                  <PlayRegular />
                )
              }
              onClick={() => handlePlayPause(music)}
              title={
                currentPlaying?.header === music.header && isPlaying ? t("PAUSE") : t("PLAY")
              }
            />

            <Button
              icon={<Dismiss20Regular />}
              appearance="subtle"
              disabled={!props.permissions?.canDelete}
              onClick={() => handleDelete(music)}
              title={t("DELETE")}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
